import type { LeaderboardEntryLocal, LeaderboardRow } from './types';
import { getSupabase } from '$db/remote/supabase';
import { getActiveUserId } from '$core/identity/active-user';
import { listMacroSessionsPage, listAttemptsByUser } from '$db/local/repositories';

export const DEBOUNCE_MS = 1000;
export const DEFAULT_SCOPE_ID = 'global';

export async function fetchLeaderboard(options: { 
  metric?: string, 
  scopeKind?: string, 
  country?: string | null, 
  limit?: number, 
  offset?: number 
} = {}): Promise<{ entries: LeaderboardRow[]; total: number }> {
  const supabase = getSupabase();
  if (!supabase) return { entries: [], total: 0 };

  const { 
    metric = 'total_minutes', 
    scopeKind = 'global', 
    country = null,
    limit = 50, 
    offset = 0 
  } = options;

  // Por ahora solo soportamos 'global' y 'country'. 
  // Friends/Groups requerirían un RPC o JOIN más complejo que omitimos para esta fase.
  if (scopeKind === 'friends' || scopeKind === 'group') {
    return { entries: [], total: 0 };
  }

  let query = supabase.from('leaderboard_entries').select('*', { count: 'exact' });

  if (scopeKind === 'country' && country) {
    query = query.eq('country', country);
  }

  // Mapear la métrica a la columna real en Supabase
  let orderColumn = 'total_minutes';
  if (metric === 'weekly') orderColumn = 'weekly_minutes';
  else if (metric === 'problems_today') orderColumn = 'problems_today';
  else if (metric === 'current_streak') orderColumn = 'current_streak';
  else if (metric === 'retention') orderColumn = 'mature_success_count';

  const { data, count, error } = await query
    .order(orderColumn, { ascending: false })
    .range(offset, offset + limit - 1);

  if (error || !data) return { entries: [], total: 0 };

  return {
    entries: data.map((row: any) => ({
      userId: row.user_id,
      displayName: row.display_name,
      country: row.country,
      weeklyMinutes: row.weekly_minutes,
      lifetimeMinutes: row.total_minutes,
      avatarUrl: row.avatar_url,
      problems_today: row.problems_today,
      minutes_today: row.minutes_today,
      current_streak: row.current_streak,
      total_minutes: row.total_minutes,
      mature_reviews_count: row.mature_reviews_count,
      mature_success_count: row.mature_success_count,
      last_seen_at: row.last_seen_at
    })),
    total: count || 0
  };
}

export async function getMyLeaderboardEntry(): Promise<LeaderboardEntryLocal | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const userId = getActiveUserId();
  
  const { data, error } = await supabase
    .from('leaderboard_entries')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    userId: data.user_id,
    displayName: data.display_name,
    country: data.country,
    weeklyMinutes: data.weekly_minutes,
    lifetimeMinutes: data.total_minutes,
    avatarUrl: data.avatar_url,
    updatedAt: data.updated_at,
    createdAt: data.created_at,
    deletedAt: data.deleted_at
  };
}

export async function flushLeaderboardUpload(input?: { userId: string; displayName: string; country: string; avatarUrl?: string | null }): Promise<void> {
  const supabase = getSupabase();
  if (!supabase || !input) return;

  // 1. Calcular estadísticas locales
  const page = await listMacroSessionsPage({ limit: 5000 });
  const sessions = page.sessions.filter(s => s.status === 'finished');
  const totalMinutes = Math.round(sessions.reduce((acc, s) => acc + s.focusDurationMs, 0) / 60000);
  
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weeklyMinutes = Math.round(sessions
    .filter(s => new Date(s.startedAt) >= weekAgo)
    .reduce((acc, s) => acc + s.focusDurationMs, 0) / 60000);

  const attempts = await listAttemptsByUser();
  const todayStr = new Date().toISOString().slice(0, 10);
  const problemsToday = attempts.filter(a => a.createdAt?.startsWith(todayStr)).length;

  // 2. Subir a Supabase
  const { error } = await supabase.from('leaderboard_entries').upsert({
    user_id: input.userId,
    display_name: input.displayName,
    country: input.country,
    weekly_minutes: weeklyMinutes,
    total_minutes: totalMinutes,
    problems_today: problemsToday,
    avatar_url: input.avatarUrl ?? null,
    updated_at: new Date().toISOString()
  }, { onConflict: 'user_id' });

  if (error) console.error('[Leaderboard] Upload failed:', error);
}

export async function heartbeatLeaderboard(): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  const userId = getActiveUserId();
  await supabase.from('leaderboard_entries').update({ last_seen_at: new Date().toISOString() }).eq('user_id', userId);
}

export async function deleteLeaderboardEntry(): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  const userId = getActiveUserId();
  await supabase.from('leaderboard_entries').delete().eq('user_id', userId);
}

export async function cancelScheduledUpload(): Promise<void> { /* no-op */ }

export interface ValidateResult {
  valid: boolean;
  reason?: 'too_short' | 'too_long' | 'invalid_chars' | 'reserved';
}

export function validateDisplayName(name: string): ValidateResult {
  const trimmed = name.trim();
  if (trimmed.length < 3) return { valid: false, reason: 'too_short' };
  if (trimmed.length > 24) return { valid: false, reason: 'too_long' };
  if (!/^[\p{L}\p{N}_\- ]+$/u.test(trimmed)) return { valid: false, reason: 'invalid_chars' };
  const reserved = ['admin', 'root', 'api'];
  if (reserved.includes(trimmed.toLowerCase())) return { valid: false, reason: 'reserved' };
  return { valid: true };
}

export async function updateDisplayName(name: string): Promise<{ ok: boolean; message?: string }> {
  const supabase = getSupabase();
  if (!supabase) return { ok: false, message: 'No connection' };
  const userId = getActiveUserId();

  const { error } = await supabase.from('leaderboard_entries').update({ display_name: name }).eq('user_id', userId);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

export async function uploadToSupabase(): Promise<void> { /* no-op */ }
export async function computeAndPersistLocal(): Promise<void> { /* no-op */ }
export async function scheduleLeaderboardUpload(): Promise<void> { /* no-op */ }

export async function clearLeaderboardAvatar(): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  const userId = getActiveUserId();
  await supabase.from('leaderboard_entries').update({ avatar_url: null }).eq('user_id', userId);
}

export async function refetchMyLeaderboardRow(): Promise<LeaderboardRow | null> {
  const row = await getMyLeaderboardEntry();
  if (!row) return null;
  return {
    userId: row.userId,
    displayName: row.displayName,
    country: row.country,
    weeklyMinutes: row.weeklyMinutes,
    lifetimeMinutes: row.lifetimeMinutes,
    avatarUrl: row.avatarUrl
  };
}